import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { DEFAULT_FORM } from "./guest-passes/constants";
import { IssuePassDialog } from "./guest-passes/IssuePassDialog";
import { GuestPassRow } from "./guest-passes/GuestPassRow";
import { SeriesList } from "./guest-passes/SeriesList";
import { QrModal } from "./guest-passes/QrModal";

/**
 * GuestPassesCard — resident-side component that owns state + API calls.
 * All rendering is delegated to focused children in `./guest-passes/`.
 *
 * Backend endpoints (under /api/me/guest-passes):
 *   GET    /api/me/guest-passes             → list mine + max_active
 *   POST   /api/me/guest-passes             → issue new (returns QR meta)
 *   POST   /api/me/guest-passes/{pid}/revoke
 *   POST   /api/me/guest-passes/{pid}/extend
 *   POST   /api/me/guest-passes/series      → issue recurring (R121)
 *   DELETE /api/me/guest-passes/series/{sid}
 */
export function GuestPassesCard() {
  const [items, setItems] = useState([]);
  const [maxActive, setMaxActive] = useState(5);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM());
  const [busy, setBusy] = useState(false);
  const [series, setSeries] = useState([]);

  // 30-second re-render so the "1h 29m left" countdown stays fresh
  // without hammering setState on every tick.
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [{ data: passes }, { data: seriesData }] = await Promise.all([
        api.get("/me/guest-passes"),
        api.get("/me/guest-passes/series").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(passes?.items || []);
      setMaxActive(passes?.max_active ?? 5);
      setSeries(seriesData?.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/GuestPassesCard.jsx: swallowed', err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const resetForm = () => setForm(DEFAULT_FORM());

  const issue = async () => {
    if (!form.guest_name.trim() || !form.plate.trim()) {
      toast.error("Guest name and plate are required");
      return;
    }
    if (form.recurring) {
      if (form.pattern === "weekly" && form.days_of_week.length === 0) {
        toast.error("Pick at least one day of the week");
        return;
      }
      setBusy(true);
      try {
        const body = {
          guest_name: form.guest_name.trim(),
          plate: form.plate.trim().toUpperCase(),
          vehicle_desc: form.vehicle_desc.trim() || undefined,
          hours: Number(form.hours),
          notes: form.notes.trim() || undefined,
          guest_phone: form.guest_phone.trim() || undefined,
          custom_note: form.custom_note.trim() || undefined,
          pattern: form.pattern,
          days_of_week: form.pattern === "weekly" ? form.days_of_week : [],
          day_of_month: form.pattern === "monthly" ? Number(form.day_of_month) : undefined,
          time_of_day: form.time_of_day,
          starts_on: form.starts_on,
          ends_on: form.ends_on || undefined,
        };
        await api.post("/me/guest-passes/series", body);
        toast.success(form.pattern === "weekly"
          ? "Series scheduled — QR auto-generates on your picked weekdays"
          : `Series scheduled — QR auto-generates every ${form.day_of_month}th`);
        setDialogOpen(false);
        resetForm();
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Series create failed");
      } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const body = {
        guest_name: form.guest_name.trim(),
        plate: form.plate.trim().toUpperCase(),
        vehicle_desc: form.vehicle_desc.trim() || undefined,
        hours: Number(form.hours),
        notes: form.notes.trim() || undefined,
        guest_phone: form.guest_phone.trim() || undefined,
        custom_note: form.custom_note.trim() || undefined,
      };
      if (form.scheduled_start_at) {
        body.scheduled_start_at = new Date(form.scheduled_start_at).toISOString();
      }
      const { data } = await api.post("/me/guest-passes", body);
      toast.success(form.scheduled_start_at
        ? `Scheduled — activates ${new Date(form.scheduled_start_at).toLocaleString()}`
        : `Pass issued — valid for ${form.hours} hours`);
      setDialogOpen(false);
      resetForm();
      setQrModal(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    } finally { setBusy(false); }
  };

  const cancelSeries = async (sid) => {
    if (!confirm("Cancel this recurring series? Future scheduled passes will be revoked.")) return;
    try {
      const { data } = await api.delete(`/me/guest-passes/series/${sid}`);
      toast.success(data?.revoked_upcoming
        ? `Series cancelled — ${data.revoked_upcoming} upcoming pass${data.revoked_upcoming !== 1 ? "es" : ""} revoked`
        : "Series cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const revoke = async (pid) => {
    // R74 optimistic UI: flip to 'revoked' immediately, rollback on error
    const snapshot = items;
    setItems((prev) => prev.map((p) => (p.id === pid ? { ...p, status: "revoked" } : p)));
    if (qrModal?.id === pid) setQrModal(null);
    try {
      await api.post(`/me/guest-passes/${pid}/revoke`);
      toast.success("Pass revoked");
      load();
    } catch (e) {
      setItems(snapshot);
      toast.error(e?.response?.data?.detail || "Revoke failed — restored");
    }
  };

  const extend = async (pid) => {
    try {
      const { data } = await api.post(`/me/guest-passes/${pid}/extend`, { hours: 24 });
      toast.success(`Extended +${data.added_hours}h — new expiry ${new Date(data.valid_until).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    }
  };

  const activeCount = items.filter((p) => p.status === "active").length;

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="guest-passes-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 overline">
          <QrCode className="h-4 w-4 text-orange-400" />
          Guest Passes ({activeCount}/{maxActive})
        </div>
        <IssuePassDialog open={dialogOpen}
                          onOpenChange={setDialogOpen}
                          form={form}
                          setForm={setForm}
                          busy={busy}
                          onSubmit={issue} />
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-400" data-testid="guest-passes-empty">
          No guest passes issued yet. Click <strong>Issue pass</strong> to create one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <GuestPassRow key={p.id}
                           pass={p}
                           tickTock={tick}
                           onShowQr={setQrModal}
                           onExtend={extend}
                           onRevoke={revoke} />
          ))}
        </div>
      )}

      <SeriesList series={series} onCancel={cancelSeries} />

      <QrModal pass={qrModal} onClose={() => setQrModal(null)} />
    </Card>
  );
}

export default GuestPassesCard;
